/**
 * AddInstrumentModal Component
 *
 * Modal for creating instrument-level fee configurations.
 * Shows a unified list of instruments from two sources:
 * - User library instruments (from InstrumentService)
 * - Master catalog instruments (from INSTRUMENT_CATALOG)
 *
 * Each item displays a chip ("Library" or "Catalog") to indicate its source.
 * Catalog instruments are imported to the user's library on selection.
 *
 * Handles two entry points with a single component:
 * - Header "Add Instrument": all instruments shown (no category filter)
 * - Row "Add Instrument" (kebab): instruments filtered by parent category
 *
 * Filtering is driven by `parentCategoryPath` prop length:
 * - [] → all active instruments shown
 * - ['futures', 'indices', 'micro'] → filtered to matching classification
 *
 * Instruments already configured for the current broker are excluded.
 * Selection keys are source-prefixed (lib:id / cat:id) to avoid collisions.
 *
 * "Create New Instrument" opens InstrumentModal for inline custom instrument
 * creation. In row-scoped mode, classification fields are locked to the parent
 * category path. After creation, the new instrument is auto-selected in the list.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
  Chip,
  Link,
  Listbox,
  ListboxItem,
  ListboxSection,
  Spinner,
  useDisclosure,
  useToastNotification,
} from '@enfomentis/ui';
import { Plus, Search } from 'lucide-react';

// 3. Internal imports
import type { Instrument } from '@/types/schema/Instrument';
import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';
import type { AssetClass, AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';
import { INSTRUMENT_CATALOG } from '@/data/instrumentCatalog';
import { instrumentService } from '@/services/InstrumentService';
import { InstrumentModal } from '@/components/shared/InstrumentExplorer/InstrumentModal';
import { useBrokers } from './context';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for AddInstrumentModal
 */
export interface AddInstrumentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Parent category path for filtering (empty array for header "Add Instrument") */
  parentCategoryPath: string[];
  /** Callback after successful add — passes new node ID for selection */
  onAdded: (nodeId: string) => void;
}

/**
 * Discriminated union for the selected item — tracks which source it came from
 */
type SelectedItem =
  | { source: 'library'; instrument: Instrument }
  | { source: 'catalog'; entry: InstrumentCatalogEntry };

/**
 * Normalized display item for the unified list
 */
interface DisplayItem {
  /** Source-prefixed key for Listbox (lib:uuid or cat:catalogId) */
  key: string;
  /** Which source this item came from */
  source: 'library' | 'catalog';
  /** Trading symbol */
  symbol: string;
  /** Human-readable name */
  displayName: string;
  /** Asset classification for grouping */
  classification: AssetClass;
  /** Raw ID for source lookup (inst.id or entry.id) */
  originalId: string;
}

// =============================================================================
// Helpers
// =============================================================================

const LIB_PREFIX = 'lib:';
const CAT_PREFIX = 'cat:';

/**
 * Parse a source-prefixed display key into source and raw ID.
 * Returns null for unknown prefixes.
 */
function parseDisplayKey(key: string): { source: 'library' | 'catalog'; id: string } | null {
  if (key.startsWith(LIB_PREFIX)) {
    return { source: 'library', id: key.slice(LIB_PREFIX.length) };
  }
  if (key.startsWith(CAT_PREFIX)) {
    return { source: 'catalog', id: key.slice(CAT_PREFIX.length) };
  }
  return null;
}

/**
 * Check whether a classification matches a parent category path
 */
function matchesCategoryPath(classification: AssetClass, path: string[]): boolean {
  if (path[0] && classification.assetClass !== path[0]) return false;
  if (path[1] && classification.marketSegment !== path[1]) return false;
  if (path[2] && classification.subAssetClass !== path[2]) return false;
  return true;
}

/**
 * Build the existing-config key for duplicate detection
 */
function buildConfigKey(classification: AssetClass, symbol: string): string {
  return `${classification.assetClass}/${classification.marketSegment}/${classification.subAssetClass ?? ''}|${symbol}`;
}

// =============================================================================
// Component
// =============================================================================

export const AddInstrumentModal: React.FC<AddInstrumentModalProps> = ({
  isOpen,
  onClose,
  parentCategoryPath,
  onAdded,
}) => {
  // ---------------------------------------------------------------------------
  // Context & Hooks
  // ---------------------------------------------------------------------------

  const { addFeeConfiguration, selectedBroker } = useBrokers();
  const { showToast } = useToastNotification();
  const navigate = useNavigate();

  // Instrument creation modal state
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Derived State (from props)
  // ---------------------------------------------------------------------------

  const modalTitle = useMemo(() => {
    if (parentCategoryPath.length === 0) return 'Add Instrument';

    // Build label from parent category path using display maps
    const labels = parentCategoryPath.map((segment, i) => {
      switch (i) {
        case 0:
          return ASSET_CLASS_TYPE_DISPLAY_MAP[segment as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP] ?? segment;
        case 1:
          return MARKET_SEGMENT_DISPLAY_MAP[segment as keyof typeof MARKET_SEGMENT_DISPLAY_MAP] ?? segment;
        case 2:
          return SUB_ASSET_CLASS_DISPLAY_MAP[segment as keyof typeof SUB_ASSET_CLASS_DISPLAY_MAP] ?? segment;
        default:
          return segment;
      }
    });
    return `Add Instrument to ${labels.join(' / ')}`;
  }, [parentCategoryPath]);

  // ---------------------------------------------------------------------------
  // Load Instruments & Reset State
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isOpen) {
      // Reset all state
      setSearchQuery('');
      setSelectedItem(null);
      setIsSubmitting(false);
      setSubmitError(null);

      // Load user library instruments
      setIsLoading(true);
      instrumentService
        .getAll()
        .then((result) => {
          setInstruments(result);
        })
        .catch((error) => {
          console.error('Failed to load instruments:', error);
          setInstruments([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Clear instruments when modal closes to free memory
      setInstruments([]);
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Filtering Logic
  // ---------------------------------------------------------------------------

  // Set of catalog IDs already imported to user library (by instrumentReferenceId)
  const libraryReferenceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const inst of instruments) {
      if (inst.instrumentReferenceId) {
        ids.add(inst.instrumentReferenceId);
      }
    }
    return ids;
  }, [instruments]);

  // Build lookup of existing instrument config keys for this broker
  const existingConfigKeys = useMemo(() => {
    if (!selectedBroker) return new Set<string>();
    const keys = new Set<string>();
    for (const config of selectedBroker.feeConfigurations) {
      const cat = config.category;
      if (config.status !== 'archived' && config.instrumentId && cat) {
        keys.add(buildConfigKey(cat, config.instrumentId));
      }
    }
    return keys;
  }, [selectedBroker]);

  // Build merged display items from both sources
  const mergedDisplayItems = useMemo(() => {
    const items: DisplayItem[] = [];

    // --- Library instruments ---
    for (const inst of instruments) {
      // Only active instruments are configurable
      if (inst.status !== 'active') continue;

      // Filter by parent category path
      if (parentCategoryPath.length > 0 && !matchesCategoryPath(inst.classification, parentCategoryPath)) continue;

      // Exclude instruments that already have active fee configs
      if (existingConfigKeys.has(buildConfigKey(inst.classification, inst.symbol))) continue;

      items.push({
        key: `${LIB_PREFIX}${inst.id}`,
        source: 'library',
        symbol: inst.symbol,
        displayName: inst.displayName,
        classification: inst.classification,
        originalId: inst.id,
      });
    }

    // --- Catalog instruments ---
    for (const entry of INSTRUMENT_CATALOG) {
      // Exclude catalog entries already imported to library
      if (libraryReferenceIds.has(entry.id)) continue;

      // Filter by parent category path
      if (parentCategoryPath.length > 0 && !matchesCategoryPath(entry.classification, parentCategoryPath)) continue;

      // Exclude entries whose symbol already has an active fee config
      if (existingConfigKeys.has(buildConfigKey(entry.classification, entry.symbol))) continue;

      items.push({
        key: `${CAT_PREFIX}${entry.id}`,
        source: 'catalog',
        symbol: entry.symbol,
        displayName: entry.name,
        classification: entry.classification,
        originalId: entry.id,
      });
    }

    return items;
  }, [instruments, parentCategoryPath, existingConfigKeys, libraryReferenceIds]);

  // Apply search filter
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return mergedDisplayItems;

    const query = searchQuery.trim().toLowerCase();
    return mergedDisplayItems.filter(
      (item) =>
        item.symbol.toLowerCase().includes(query) ||
        item.displayName.toLowerCase().includes(query)
    );
  }, [mergedDisplayItems, searchQuery]);

  // Group filtered items by asset class for display sections
  const groupedItems = useMemo(() => {
    const groups = new Map<string, DisplayItem[]>();
    for (const item of filteredItems) {
      const groupKey = item.classification.assetClass;
      const group = groups.get(groupKey);
      if (group) {
        group.push(item);
      } else {
        groups.set(groupKey, [item]);
      }
    }
    return groups;
  }, [filteredItems]);

  // ---------------------------------------------------------------------------
  // Stale-Selection Guard
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Skip during initial instrument loading (no data yet)
    if (instruments.length === 0 && isLoading) return;

    if (selectedItem) {
      const selectedKey =
        selectedItem.source === 'library'
          ? `${LIB_PREFIX}${selectedItem.instrument.id}`
          : `${CAT_PREFIX}${selectedItem.entry.id}`;

      if (!filteredItems.some((item) => item.key === selectedKey)) {
        setSelectedItem(null);
      }
    }
  }, [filteredItems, selectedItem, instruments.length, isLoading]);

  // ---------------------------------------------------------------------------
  // Selection Handler
  // ---------------------------------------------------------------------------

  const handleSelectionChange = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const rawKey = Array.from(keys)[0] as string | undefined;
      if (!rawKey) {
        setSelectedItem(null);
        return;
      }

      const parsed = parseDisplayKey(rawKey);
      if (!parsed) {
        // Unknown prefix — clear selection
        setSelectedItem(null);
        return;
      }

      if (parsed.source === 'library') {
        const found = instruments.find((inst) => inst.id === parsed.id);
        setSelectedItem(found ? { source: 'library', instrument: found } : null);
      } else {
        const found = INSTRUMENT_CATALOG.find((entry) => entry.id === parsed.id);
        setSelectedItem(found ? { source: 'catalog', entry: found } : null);
      }
      setSubmitError(null);
    },
    [instruments]
  );

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const isFormValid = selectedItem !== null && !isSubmitting && !!selectedBroker;

  // ---------------------------------------------------------------------------
  // Instrument Creation (InstrumentModal integration)
  // ---------------------------------------------------------------------------

  // Derive initialContext for InstrumentModal from parentCategoryPath
  const createInitialContext = useMemo(() => {
    if (parentCategoryPath.length === 0) return undefined;
    return {
      ...(parentCategoryPath[0] && { assetClass: parentCategoryPath[0] as AssetClassType }),
      ...(parentCategoryPath[1] && { marketSegment: parentCategoryPath[1] as MarketSegment }),
      ...(parentCategoryPath[2] && { subAssetClass: parentCategoryPath[2] as SubAssetClass }),
    };
  }, [parentCategoryPath]);

  // Lock classification fields when opened from a category row
  const classificationLocked = parentCategoryPath.length > 0;

  /**
   * Handle successful instrument creation from InstrumentModal.
   * Reloads the instrument list and auto-selects the new instrument by ID.
   */
  const handleInstrumentCreated = useCallback(
    async (instrument: Instrument): Promise<void> => {
      onCreateClose();
      setSubmitError(null);

      try {
        const reloaded = await instrumentService.getAll();
        setInstruments(reloaded);

        // Find the newly created instrument from the reloaded list (avoids stale object)
        const reloadedInstrument = reloaded.find((inst) => inst.id === instrument.id);
        if (reloadedInstrument) {
          setSelectedItem({ source: 'library', instrument: reloadedInstrument });
        } else {
          // Defensive: instrument was created but doesn't appear in filtered list
          setSubmitError(
            'Instrument was created but doesn\'t match the current category filter. You can find it in the Instruments tab.'
          );
        }
      } catch (error) {
        console.error('Failed to reload instruments after creation:', error);
        setSubmitError('Instrument was created but the list failed to refresh. Please close and reopen.');
      }
    },
    [onCreateClose]
  );

  /**
   * Handle instrument creation error from InstrumentModal.
   */
  const handleInstrumentError = useCallback((error: string): void => {
    setSubmitError(error);
  }, []);

  /**
   * Navigate to the Instruments tab for bulk management.
   * Closes both modal layers before navigating.
   */
  const handleManageInstruments = useCallback((): void => {
    if (isSubmitting) return; // Guard against navigation during in-flight submit
    onCreateClose(); // Safe no-op if not open
    onClose();
    navigate('/settings/portfolio/reference-data?tab=instruments');
  }, [isSubmitting, onCreateClose, onClose, navigate]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!selectedItem) return;
    if (!selectedBroker) {
      setSubmitError('No broker selected.');
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      let symbol: string;
      let classification: AssetClass;

      if (selectedItem.source === 'library') {
        // Library instrument — use directly
        symbol = selectedItem.instrument.symbol;
        classification = selectedItem.instrument.classification;
      } else {
        // Catalog instrument — import to library first
        const importResult = await instrumentService.importFromCatalog([selectedItem.entry.id]);

        const created = importResult.created[0];
        const duplicate = importResult.duplicates[0];

        if (created) {
          symbol = created.symbol;
          classification = created.classification;
        } else if (duplicate) {
          // Already imported (race or stale UI) — use existing
          symbol = duplicate.existingInstrument.symbol;
          classification = duplicate.existingInstrument.classification;
        } else {
          // Import failed — show error from result
          const errorMsg = importResult.errors[0]?.error ?? 'Unknown import error';
          setSubmitError(`Failed to import instrument: ${errorMsg}`);
          return;
        }
      }

      // Create fee configuration
      const result = await addFeeConfiguration({
        category: classification,
        instrumentId: symbol,
      });

      if (result.success) {
        const toastDesc =
          selectedItem.source === 'catalog'
            ? `${symbol} imported to library and fee configuration created.`
            : `Fee configuration created for ${symbol}.`;

        showToast({
          title: 'Instrument fee configuration added',
          description: toastDesc,
          color: 'success',
        });
        onAdded(result.nodeId);
        onClose();
      } else {
        // Exhaustive handling of all failure reasons
        switch (result.reason) {
          case 'active_exists':
            setSubmitError(
              `A fee configuration already exists for ${symbol}.`
            );
            break;
          case 'no_broker':
            setSubmitError('No broker selected.');
            break;
          case 'invalid_path':
            setSubmitError('Invalid instrument classification.');
            break;
          case 'unknown_error':
            setSubmitError('An unexpected error occurred. Please try again.');
            break;
          default: {
            const _exhaustive: never = result.reason;
            return _exhaustive;
          }
        }
      }
    } catch (error) {
      console.error('Failed to add instrument fee configuration:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedItem, selectedBroker, addFeeConfiguration, showToast, onAdded, onClose]);

  // ---------------------------------------------------------------------------
  // Selected key for Listbox
  // ---------------------------------------------------------------------------

  const selectedKeys = useMemo(() => {
    if (!selectedItem) return new Set<string>();
    if (selectedItem.source === 'library') {
      return new Set([`${LIB_PREFIX}${selectedItem.instrument.id}`]);
    }
    return new Set([`${CAT_PREFIX}${selectedItem.entry.id}`]);
  }, [selectedItem]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <Modal
        isOpen={isOpen && !isCreateOpen}
        onOpenChange={(open) => !open && onClose()}
        size="md"
        placement="center"
        isDismissable={!isSubmitting}
        hideCloseButton={isSubmitting}
      >
        <ModalContent>
          <ModalHeader>{modalTitle}</ModalHeader>

          <ModalBody className="gap-4">
            {/* Defensive: no broker selected */}
            {!selectedBroker ? (
              <p className="text-text-secondary text-sm">
                No broker selected. Please select a broker first.
              </p>
            ) : (
              <>
                {/* Search Input */}
                <Input
                  placeholder="Search by symbol or name..."
                  size="sm"
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  startContent={<Search className="w-4 h-4 text-text-secondary" />}
                  isClearable
                />

                {/* Create New / Manage Instruments actions */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="light"
                    size="md"
                    startContent={<Plus className="w-4 h-4" />}
                    onPress={onCreateOpen}
                    isDisabled={isSubmitting}
                    className="bg-default-200"
                  >
                    Create New Instrument
                  </Button>
                  <Link
                    onPress={handleManageInstruments}
                    isDisabled={isSubmitting}
                    showAnchorIcon
                    size="sm"
                    className="cursor-pointer data-[disabled=true]:cursor-not-allowed"
                  >
                    Manage Instruments
                  </Link>
                </div>

                {/* Instrument List */}
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="md" />
                  </div>
                ) : filteredItems.length === 0 ? (
                  <p className="text-text-tertiary text-sm text-center py-6">
                    {mergedDisplayItems.length === 0
                      ? 'No instruments available.'
                      : searchQuery.trim()
                        ? 'No instruments match your search.'
                        : 'All matching instruments are already configured.'}
                  </p>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-default-200 rounded-lg">
                    <Listbox
                      aria-label="Select instrument"
                      selectionMode="single"
                      selectedKeys={selectedKeys}
                      onSelectionChange={handleSelectionChange}
                    >
                      {Array.from(groupedItems.entries()).map(
                        ([assetClass, groupItems]) => (
                          <ListboxSection
                            key={assetClass}
                            title={
                              ASSET_CLASS_TYPE_DISPLAY_MAP[
                                assetClass as keyof typeof ASSET_CLASS_TYPE_DISPLAY_MAP
                              ] ?? assetClass
                            }
                          >
                            {groupItems.map((item) => (
                              <ListboxItem
                                key={item.key}
                                textValue={`${item.symbol} ${item.displayName}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex flex-col min-w-0">
                                    <span className="font-medium">{item.symbol}</span>
                                    <span className="text-xs text-text-tertiary truncate">
                                      {item.displayName}
                                    </span>
                                  </div>
                                  <Chip
                                    size="sm"
                                    variant="flat"
                                    color={item.source === 'library' ? 'default' : 'secondary'}
                                  >
                                    {item.source === 'library' ? 'Library' : 'Catalog'}
                                  </Chip>
                                </div>
                              </ListboxItem>
                            ))}
                          </ListboxSection>
                        )
                      )}
                    </Listbox>
                  </div>
                )}
              </>
            )}

            {/* Error Alert */}
            {submitError && (
              <Alert color="danger" variant="flat">
                {submitError}
              </Alert>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!isFormValid}
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Instrument creation modal (sibling, not nested) */}
      <InstrumentModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSave={handleInstrumentCreated}
        onError={handleInstrumentError}
        {...(createInitialContext && { initialContext: createInitialContext })}
        classificationLocked={classificationLocked}
      />
    </>
  );
};

export default AddInstrumentModal;
